/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, Typography, Divider, IconButton, Checkbox } from "@mui/material";
import Button from "@mui/material/Button";
import "./DomainApproverTargetingMmyPopUp.css";

const getItemKey = (makeName, modelName, year) =>
  `${makeName}_${modelName}_${year}`;

const markAllUnchecked = (mergedData) => {
  const checkedItems = {};
  mergedData.forEach((makeItem) => {
    makeItem.models.forEach((modelItem) => {
      modelItem.years.forEach((year) => {
        checkedItems[getItemKey(makeItem.make, modelItem.name, year)] = false;
      });
    });
  });
  return checkedItems;
};

const setMakeModelsYearsChecked = (checkedItems, targetMake) => {
  targetMake.models.forEach((modelItem) => {
    modelItem.years.forEach((year) => {
      checkedItems[getItemKey(targetMake.make, modelItem.name, year)] = true;
    });
  });
};

const setModelYearsChecked = (checkedItems, targetMake, targetModel) => {
  targetModel.years.forEach((year) => {
    checkedItems[getItemKey(targetMake.make, targetModel.name, year)] = true;
  });
};

const applyInclusionSelection = (checkedItems, inclusion, mergedData) => {
  const makeData = inclusion.make;
  const modelData = inclusion.model;
  const yearData = inclusion.year;

  if (makeData.all && modelData.all && yearData.all) {
    mergedData.forEach((makeItem) => {
      setMakeModelsYearsChecked(checkedItems, makeItem);
    });
    return;
  }

  if (makeData.all || modelData.all && !yearData.all) {
    return;
  }

  const targetMake = mergedData.find((makeItem) => makeItem.id === makeData.id);
  if (!targetMake) {
    return;
  }

  if (modelData.all && yearData.all) {
    setMakeModelsYearsChecked(checkedItems, targetMake);
    return;
  }

  const targetModel = targetMake.models.find(
    (modelItem) => modelItem.id === modelData.id
  );
  if (!targetModel) {
    return;
  }

  if (yearData.all) {
    setModelYearsChecked(checkedItems, targetMake, targetModel);
    return;
  }

  checkedItems[getItemKey(targetMake.make, targetModel.name, yearData.year)] = true;
};

const convertInclusionsToCheckedItems = (inclusions, mergedData) => {
  const checkedItems = markAllUnchecked(mergedData);
  inclusions.forEach((inclusion) => {
    applyInclusionSelection(checkedItems, inclusion, mergedData);
  });
  return checkedItems;
};

const ensureMakeModelInMap = (tempMap, makeName, makeId, modelName, modelId) => {
  if (!tempMap.has(makeName)) {
    tempMap.set(makeName, { id: makeId, models: new Map() });
  }

  const makeEntry = tempMap.get(makeName);
  if (!makeEntry.models.has(modelName)) {
    makeEntry.models.set(modelName, { id: modelId, years: new Set() });
  }
};

const buildTempMapFromMmyData = (mmyData) => {
  const tempMap = new Map();

  mmyData.forEach((makeItem) => {
    makeItem.models.forEach((modelItem) => {
      ensureMakeModelInMap(
        tempMap,
        makeItem.make,
        makeItem.id,
        modelItem.name,
        modelItem.id
      );

      const modelEntry = tempMap.get(makeItem.make).models.get(modelItem.name);
      modelItem.years.forEach((year) => {
        modelEntry.years.add(year);
      });
    });
  });

  return tempMap;
};

const addExclusionsToTempMap = (tempMap, mmyExclusion) => {
  mmyExclusion.forEach((entry) => {
    if (typeof entry !== "string" || !entry.includes("_")) {
      console.warn("Invalid exclusion entry format:", entry);
      return;
    }

    const [makeName, modelName, yearStr] = entry.split("_");
    const year = Number.parseInt(yearStr, 10);

    ensureMakeModelInMap(tempMap, makeName, null, modelName, null);
    tempMap.get(makeName).models.get(modelName).years.add(year);
  });
};

const mapToMergedData = (tempMap) =>
  Array.from(tempMap.entries()).map(([make, makeData]) => ({
    make,
    id: makeData.id,
    models: Array.from(makeData.models.entries()).map(([model, modelData]) => ({
      name: model,
      id: modelData.id,
      years: Array.from(modelData.years).sort((a, b) => a - b),
    })),
  }));

const buildCheckedFromExclusions = (tempMap, exclusionSet) => {
  const initialChecked = {};
  tempMap.forEach((makeData, make) => {
    makeData.models.forEach((modelData, model) => {
      modelData.years.forEach((year) => {
        const key = getItemKey(make, model, year);
        initialChecked[key] = !exclusionSet.has(key);
      });
    });
  });
  return initialChecked;
};

const buildDefaultChecked = (tempMap, isThirdPartyApp) => {
  const initialChecked = {};
  tempMap.forEach((makeData, make) => {
    makeData.models.forEach((modelData, model) => {
      modelData.years.forEach((year) => {
        initialChecked[getItemKey(make, model, year)] = isThirdPartyApp;
      });
    });
  });
  return initialChecked;
};

const summarizeModelSelection = (make, model, checkedItems) => {
  const modelObjIncl = {
    id: model.id,
    years: [],
    allYearsSelected: false,
  };
  const uncheckedYears = [];

  model.years.forEach((year) => {
    const key = getItemKey(make.make, model.name, year);
    if (checkedItems[key]) {
      modelObjIncl.years.push(year);
    } else {
      uncheckedYears.push(year);
    }
  });

  const checkedYearCount = modelObjIncl.years.length;
  if (checkedYearCount === model.years.length && checkedYearCount > 0) {
    modelObjIncl.allYearsSelected = true;
  }

  return {
    modelObjIncl,
    uncheckedYears,
    hasCheckedYears: checkedYearCount > 0,
    hasUncheckedYears: uncheckedYears.length > 0,
  };
};

const formatExclusionRows = (make) =>
  make.models.map((exclusionItem) => ({
    make: { id: make.id, all: false },
    model: { id: exclusionItem.id, all: false },
    year: { year: Number.parseInt(exclusionItem.year, 10), all: false },
  }));

const formatInclusionRows = (make, model) => {
  if (model.allYearsSelected) {
    return [
      {
        make: { id: make.id, all: false },
        model: { id: model.id, all: false },
        year: { year: null, all: true },
      },
    ];
  }

  return model.years.map((year) => ({
    make: { id: make.id, all: false },
    model: { id: model.id, all: false },
    year: { year: Number.parseInt(year, 10), all: false },
  }));
};

function DomainApproverTargetingMmyPopUp({
  mmyData = [],
  mmyExclusion = [],
  mmyInclusion = [],
  mmySave,
  onClose,
  reviewStatus = [],
  readOnly = false,
  isThirdPartyApp = false,
}) {
  const [mergedData, setMergedData] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  const boxStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    backgroundColor: "#F3F3F3",
    borderRadius: "8px",
    width: "215px",
    height: "278px",
    padding: "8px",
  };

  const itemStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const cancelButtonStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "36px",
    width: "114px",
    backgroundColor: "#1214151A",
    borderRadius: "4px",
    cursor: "pointer",
  };

  const saveButtonStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "36px",
    width: "114px",
    backgroundColor: "#0476E2",
    borderRadius: "4px",
    cursor: "pointer",
  };

  const isRestricted =
    readOnly ||
    reviewStatus.includes("FORCE_UNINSTALL") ||
    reviewStatus.includes("DELIST");

  useEffect(() => {
    const exclusionSet = new Set(mmyExclusion);
    const tempMap = buildTempMapFromMmyData(mmyData);
    addExclusionsToTempMap(tempMap, mmyExclusion);
    const merged = mapToMergedData(tempMap);

    setMergedData(merged);

    // Use inclusion data if available, otherwise fall back to exclusion logic
    let initialChecked = {};
    if (mmyInclusion && mmyInclusion.length > 0) {
      initialChecked = convertInclusionsToCheckedItems(mmyInclusion, merged);
    } else if (mmyExclusion && mmyExclusion.length > 0) {
      initialChecked = buildCheckedFromExclusions(tempMap, exclusionSet);
    } else {
      initialChecked = buildDefaultChecked(tempMap, isThirdPartyApp);
    }

    setCheckedItems(initialChecked);

    if (merged.length > 0) {
      setSelectedMake(merged[0].make);
      if (merged[0].models.length > 0) {
        setSelectedModel(merged[0].models[0].name);
      }
    }
  }, [mmyData, mmyExclusion, mmyInclusion, isThirdPartyApp]);

  const handleCheckboxChange = (key) => {
    setCheckedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleMakeCheckboxChange = (make) => {
    const allKeys = make.models.flatMap((model) =>
      model.years.map((year) => `${make.make}_${model.name}_${year}`)
    );
    const shouldCheck = allKeys.some((key) => !checkedItems[key]);
    const updated = { ...checkedItems };
    allKeys.forEach((key) => {
      updated[key] = shouldCheck;
    });
    setCheckedItems(updated);
  };

  const handleModelCheckboxChange = (makeName, model) => {
    const allKeys = model.years.map(
      (year) => `${makeName}_${model.name}_${year}`
    );
    const shouldCheck = allKeys.some((key) => !checkedItems[key]);
    const updated = { ...checkedItems };
    allKeys.forEach((key) => {
      updated[key] = shouldCheck;
    });
    setCheckedItems(updated);
  };

  const getMakeCheckboxState = (make) => {
    const allKeys = make.models.flatMap((model) =>
      model.years.map((year) => `${make.make}_${model.name}_${year}`)
    );
    const checkedCount = allKeys.filter((key) => checkedItems[key]).length;

    if (checkedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (checkedCount === allKeys.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  const getModelCheckboxState = (makeName, model) => {
    const allKeys = model.years.map(
      (year) => `${makeName}_${model.name}_${year}`
    );
    const checkedCount = allKeys.filter((key) => checkedItems[key]).length;

    if (checkedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (checkedCount === allKeys.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  const handleClearAll = () => {
    const cleared = {};
    Object.keys(checkedItems).forEach((key) => {
      cleared[key] = false;
    });
    setCheckedItems(cleared);
  };
  const buildVehicleSelectionsFromCheckedItems = () => {
    const inclusions = {
      makes: [],
      allMakesSelected: false,
    };
    const exclusions = {
      makes: [],
      allMakesSelected: false,
    };

    let allChecked = true;
    let allUnchecked = true;

    mergedData.forEach((make) => {
      const makeObjIncl = {
        id: make.id,
        models: [],
        allModelsSelected: false,
      };

      const totalModels = make.models.length;
      let includedModelCount = 0;
      let hasAnyCheckedInMake = false;

      // Track exclusions for this make
      let makeExclusions = [];

      make.models.forEach((model) => {
        const {
          modelObjIncl,
          uncheckedYears,
          hasCheckedYears,
          hasUncheckedYears,
        } = summarizeModelSelection(make, model, checkedItems);

        if (hasCheckedYears) {
          makeObjIncl.models.push(modelObjIncl);
          includedModelCount++;
          hasAnyCheckedInMake = true;
          allUnchecked = false;
        }

        if (hasUncheckedYears) {
          allChecked = false;
          for (const year of uncheckedYears) {
            makeExclusions.push({
              id: model.id,
              year,
            });
          }
        }
      });

      // Check if all models are fully selected with all years
      const allModelsIncluded =
        includedModelCount === totalModels &&
        makeObjIncl.models.every((m) => m.allYearsSelected);

      if (allModelsIncluded) {
        makeObjIncl.allModelsSelected = true;
        makeObjIncl.models = []; // Don't send individual models if ALL are selected
      }

      // Only add to inclusions if this make has any checked items
      if (hasAnyCheckedInMake) {
        inclusions.makes.push(makeObjIncl);

        // Add exclusions for this make if there are any unchecked items
        if (makeExclusions.length > 0) {
          exclusions.makes.push({
            id: make.id,
            models: makeExclusions,
            allModelsSelected: false,
          });
        }
      }
    });

    // Entire dataset selected/unchecked
    if (allChecked) {
      inclusions.allMakesSelected = true;
      inclusions.makes = [];
      exclusions.makes = []; // Clear exclusions when all selected
    } else if (allUnchecked) {
      inclusions.makes = [];
      exclusions.makes = [];
    }

    return { inclusions, exclusions };
  };
  const handleSave = () => {
    const { inclusions, exclusions } = buildVehicleSelectionsFromCheckedItems();

    const formattedInclusions = getFormattedTargetingData(
      inclusions,
      "inclusion"
    );
    const formattedExclusions = getFormattedTargetingData(
      exclusions,
      "exclusion"
    );

    mmySave("mmy_inclusion", {
      vehicleInclusions: formattedInclusions,
      vehicleExclusions: formattedExclusions,
    });
  };
  const getFormattedTargetingData = (selectedVehicles, type) => {
    const result = [];

    if (selectedVehicles.allMakesSelected) {
      result.push({
        make: { id: null, all: true },
        model: { id: null, all: true },
        year: { year: null, all: true },
      });
      return result;
    }

    selectedVehicles.makes.forEach((make) => {
      if (make.allModelsSelected) {
        // Scenario 3: One Make Selected with All Models and Years
        result.push({
          make: { id: make.id, all: false },
          model: { id: null, all: true },
          year: { year: null, all: true },
        });
      } else if (type === "exclusion") {
        result.push(...formatExclusionRows(make));
      } else {
        make.models.forEach((model) => {
          result.push(...formatInclusionRows(make, model));
        });
      }
    });

    return result;
  };

  const currentMake = mergedData.find((m) => m.make === selectedMake);
  const currentModel = currentMake?.models.find(
    (m) => m.name === selectedModel
  );
  const isSingleModelUnselected = Object.values(checkedItems).every(
    (v) => v === false
  );

  return (
    <div className="mmy-popup">
      <div
        className="mmy-popup-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "-10px",
          marginBottom: "6px",
        }}
      >
        <Typography variant="h6">Targeting - Make/Model/Year</Typography>
        <IconButton onClick={onClose}>
          <ClearIcon sx={{ color: "#2A2C2D" }} />
        </IconButton>
      </div>

      <div
        className="mmy-popup-content"
        style={{ display: "flex", gap: "16px" }}
      >
        <Box sx={boxStyle}>
          <Typography variant="subtitle1" fontWeight="bold">
            Make ({mergedData.length})
          </Typography>
          <Divider />
          <div className="mmy-models-content">
            {mergedData.map((make) => {
              const { checked, indeterminate } = getMakeCheckboxState(make);
              return (
                <Box
                  key={make.make}
                  sx={{
                    ...itemStyle,
                    backgroundColor:
                      selectedMake === make.make ? "#D6E9F8" : "transparent",
                    cursor: "pointer",
                    borderRadius: "4px",
                    padding: "4px",
                  }}
                  onClick={() => {
                    setSelectedMake(make.make);
                    setSelectedModel(make.models[0]?.name || null);
                  }}
                >
                  <Checkbox
                    checked={checked}
                    indeterminate={indeterminate}
                    disabled={isRestricted}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMakeCheckboxChange(make);
                    }}
                  />
                  <Typography variant="subtitle1">{make.make}</Typography>
                </Box>
              );
            })}
          </div>
        </Box>

        <Box sx={boxStyle}>
          <Typography variant="subtitle1" fontWeight="bold">
            Model ({currentMake?.models.length || 0})
          </Typography>
          <Divider />
          <div className="mmy-models-content">
            {currentMake?.models.map((model) => {
              const { checked, indeterminate } = getModelCheckboxState(
                currentMake.make,
                model
              );
              return (
                <Box
                  key={model.name}
                  sx={{
                    ...itemStyle,
                    backgroundColor:
                      selectedModel === model.name ? "#D6E9F8" : "transparent",
                    cursor: "pointer",
                    borderRadius: "4px",
                    padding: "4px",
                  }}
                  onClick={() => setSelectedModel(model.name)}
                >
                  <Checkbox
                    checked={checked}
                    indeterminate={indeterminate}
                    disabled={isRestricted}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModelCheckboxChange(currentMake.make, model);
                    }}
                  />
                  <Typography variant="subtitle1">{model.name}</Typography>
                </Box>
              );
            })}
          </div>
        </Box>

        <Box sx={boxStyle}>
          <Typography variant="subtitle1" fontWeight="bold">
            Year ({currentModel?.years.length || 0})
          </Typography>
          <Divider />
          <div className="mmy-models-content">
            {currentModel?.years.map((year) => {
              const key = `${selectedMake}_${selectedModel}_${year}`;
              return (
                <Box key={key} sx={itemStyle}>
                  <Checkbox
                    checked={!!checkedItems[key]}
                    onChange={() => handleCheckboxChange(key)}
                    disabled={isRestricted}
                  />
                  <Typography variant="subtitle1">{year}</Typography>
                </Box>
              );
            })}
          </div>
        </Box>
      </div>

      <div
        className="mmy-popup-content-footer"
        style={{
          marginTop: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          onClick={isRestricted ? undefined : handleClearAll}
          sx={{
            textTransform: "none",
            fontWeight: 400,
            mr: "auto",
            color: "#5A5B5C",
            opacity: isRestricted ? 0.5 : 1,
            pointerEvents: isRestricted ? "none" : "auto",
          }}
        >
          Clear All
        </Button>

        <div style={{ display: "flex", gap: "12px" }}>
          <Box sx={cancelButtonStyle} onClick={onClose}>
            <Typography sx={{ color: "#2A2C2D" }}>Cancel</Typography>
          </Box>

          {!isRestricted && (
            <Box
              sx={{
                ...saveButtonStyle,
                opacity: isSingleModelUnselected ? 0.5 : 1,
                pointerEvents: isSingleModelUnselected ? "none" : "auto",
              }}
              onClick={isSingleModelUnselected ? undefined : handleSave}
            >
              <Typography sx={{ color: "#FFFFFF" }}>Save</Typography>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
}

export default DomainApproverTargetingMmyPopUp;

DomainApproverTargetingMmyPopUp.propTypes = {
  mmyData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      make: PropTypes.string,
      models: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          name: PropTypes.string,
          years: PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          ),
        })
      ),
    })
  ),
  mmyExclusion: PropTypes.arrayOf(PropTypes.string),
  mmyInclusion: PropTypes.arrayOf(
    PropTypes.shape({
      make: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        all: PropTypes.bool,
      }),
      model: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        all: PropTypes.bool,
      }),
      year: PropTypes.shape({
        year: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.oneOf([null])]),
        all: PropTypes.bool,
      }),
    })
  ),
  mmySave: PropTypes.func,
  onClose: PropTypes.func,
  reviewStatus: PropTypes.arrayOf(PropTypes.string),
  readOnly: PropTypes.bool,
  isThirdPartyApp: PropTypes.bool,
};
